import { actionTypes } from 'redux/actions/notifications'
import { INotificationListState, INotificationsState, INotification } from 'config/notificationTypes'

const emptyList: INotificationListState = {
  items: [],
  nextCursor: null,
  isLoading: false,
  hasLoadedOnce: false,
}

export const initialState: INotificationsState = {
  inbox: emptyList,
  unread: emptyList,
  unreadCount: 0,
}

const isNowSeen = (item: { chrono_key: string }, seenKeys: string[] | null, markAll: boolean) =>
  markAll || !!seenKeys?.includes(item.chrono_key)

// `inbox` keeps every item (just flips its `seen` flag); `unread` is the sparse "still unread"
// index, so a newly-seen item is dropped from it entirely, mirroring GET /unread server-side.
const applySeenToInbox = (list: INotificationListState, seenKeys: string[] | null, markAll: boolean) => {
  if (!markAll && !seenKeys?.length) return list
  return {
    ...list,
    items: list.items.map((item) => (isNowSeen(item, seenKeys, markAll) ? { ...item, seen: true } : item)),
  }
}

const applySeenToUnread = (list: INotificationListState, seenKeys: string[] | null, markAll: boolean) => {
  if (!markAll && !seenKeys?.length) return list
  return { ...list, items: list.items.filter((item) => !isNowSeen(item, seenKeys, markAll)) }
}

const notificationsReducer = (state = initialState, action: any): INotificationsState => {
  switch (action.type) {
    case actionTypes.SET_LIST_LOADING: {
      const current = state[action.list as 'inbox' | 'unread'] || emptyList
      return { ...state, [action.list]: { ...current, isLoading: action.isLoading } }
    }

    case actionTypes.SET_LIST: {
      const current = state[action.list as 'inbox' | 'unread'] || emptyList
      const items = action.append ? [...current.items, ...action.items] : action.items
      return {
        ...state,
        [action.list]: {
          items,
          nextCursor: action.nextCursor,
          isLoading: false,
          hasLoadedOnce: true,
        },
      }
    }

    case actionTypes.SET_UNREAD_COUNT:
      return { ...state, unreadCount: action.unreadCount }

    case actionTypes.MARK_SEEN_OPTIMISTIC: {
      const unreadCount = action.markAll ? 0 : Math.max(0, state.unreadCount - (action.seenKeys?.length || 0))
      return {
        ...state,
        inbox: applySeenToInbox(state.inbox, action.seenKeys, action.markAll),
        unread: applySeenToUnread(state.unread, action.seenKeys, action.markAll),
        unreadCount,
      }
    }

    case actionTypes.RECEIVE_LIVE_NOTIFICATION: {
      const notification = action.notification as INotification
      const prepend = (list: INotificationListState): INotificationListState => {
        if (list.items.some((item) => item.chrono_key === notification.chrono_key)) return list
        return { ...list, items: [notification, ...list.items] }
      }
      return {
        ...state,
        inbox: prepend(state.inbox),
        unread: notification.seen ? state.unread : prepend(state.unread),
      }
    }

    case actionTypes.RESTORE_STATE:
      return action.state ?? state

    default:
      return state
  }
}

export default notificationsReducer
